import React, { useState, useEffect } from 'react';
import type { DoctorCertification, Specialization, Subspecialization } from '../../../types/doctorOnboarding';
import { uploadFile } from '../../../services/doctor-onboarding/doctorOnboardingService';
import { formatStepError } from './stepError';
import { getFileNameFromUrl } from './fileName';

type Props = {
  doctorId: number | null;
  entries: DoctorCertification[];
  certificationTypes: Array<{ id: number; name: string; code: string; is_active: boolean }>;
  specializations: Specialization[];
  subspecializations: Subspecialization[];
  onLoadSubspecializations: (specializationId: number) => Promise<void>;
  onAdd: (body: Omit<DoctorCertification, 'id'>) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
  onNext: () => void;
  onBack: () => void;
};

export default function CertificationsStep({
  doctorId,
  entries,
  certificationTypes,
  specializations,
  subspecializations,
  onLoadSubspecializations,
  onAdd,
  onDelete,
  onNext,
  onBack,
}: Props) {
  const [certTypeId, setCertTypeId] = useState<number | ''>('');
  const [name, setName] = useState('');
  const [org, setOrg] = useState('');
  const [issueYear, setIssueYear] = useState<number | ''>('');
  const [expiryYear, setExpiryYear] = useState<number | ''>('');
  const [certNumber, setCertNumber] = useState('');
  const [specializationId, setSpecializationId] = useState<number | ''>('');
  const [subspecializationId, setSubspecializationId] = useState<number | ''>('');
  const [file, setFile] = useState<File | null>(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (specializationId !== '') {
      onLoadSubspecializations(Number(specializationId));
      setSubspecializationId('');
    }
  }, [specializationId, onLoadSubspecializations]);

  const isValid =
    !!doctorId &&
    certTypeId !== '' &&
    name.trim() &&
    org.trim() &&
    issueYear !== '' &&
    !!file;

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || !doctorId) return;
    setLoading(true);
    setError(null);
    try {
      let url: string | undefined;
      if (file) {
        const upload = await uploadFile(file, 'certifications');
        url = upload.url;
      }
      await onAdd({
        doctor_id: doctorId,
        certification_type_id: Number(certTypeId),
        certification_name: name.trim(),
        issuing_organization: org.trim(),
        issue_date: Number(issueYear),
        expiry_date: expiryYear === '' ? undefined : Number(expiryYear),
        certificate_number: certNumber.trim() || undefined,
        certificate_document_url: url,
        specialization_id: specializationId === '' ? undefined : Number(specializationId),
        subspecialization_id: subspecializationId === '' ? undefined : Number(subspecializationId),
      } as DoctorCertification);
      setCertTypeId('');
      setName('');
      setOrg('');
      setIssueYear('');
      setExpiryYear('');
      setCertNumber('');
      setSpecializationId('');
      setSubspecializationId('');
      setFile(null);
      setFileInputKey((prev) => prev + 1);
    } catch (e: any) {
      setError(formatStepError(e, 'Failed to add certification'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="onboarding-section">
        <h3>Certifications (Optional)</h3>
        <form onSubmit={handleAdd}>
          <div className="form-group">
            <label>Certification Type</label>
            <select
              value={certTypeId === '' ? '' : certTypeId}
              onChange={(e) =>
                setCertTypeId(e.target.value === '' ? '' : Number(e.target.value))
              }
            >
              <option value="">Select</option>
              {certificationTypes.filter((c) => c.is_active).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Certification Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Specialization (optional)</label>
            <select
              value={specializationId === '' ? '' : specializationId}
              onChange={(e) => setSpecializationId(e.target.value === '' ? '' : Number(e.target.value))}
            >
              <option value="">Select</option>
              {specializations.filter((s) => s.is_active).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Subspecialization (optional)</label>
            <select
              value={subspecializationId === '' ? '' : subspecializationId}
              onChange={(e) => setSubspecializationId(e.target.value === '' ? '' : Number(e.target.value))}
              disabled={specializationId === ''}
            >
              <option value="">Select</option>
              {subspecializations.filter((s) => s.is_active).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Issuing Organization</label>
            <input value={org} onChange={(e) => setOrg(e.target.value)} />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Issue Year</label>
              <input
                type="number"
                min={1950}
                max={2100}
                value={issueYear}
                onChange={(e) => setIssueYear(e.target.value === '' ? '' : Number(e.target.value))}
              />
            </div>
            <div className="form-group">
              <label>Expiry Year (optional)</label>
              <input
                type="number"
                min={1950}
                max={2100}
                value={expiryYear}
                onChange={(e) =>
                  setExpiryYear(e.target.value === '' ? '' : Number(e.target.value))
                }
              />
            </div>
          </div>
          <div className="form-group">
            <label>Certificate Number (optional)</label>
            <input value={certNumber} onChange={(e) => setCertNumber(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Certificate Upload (optional)</label>
            <input
              key={fileInputKey}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </div>
          {error && <div className="onboarding-error">{error}</div>}
          <button type="submit" className="btn-primary" disabled={!isValid || loading}>
            {loading ? 'Saving...' : 'Add Certification'}
          </button>
        </form>
      </div>

      <div className="onboarding-section">
        <h3>Existing Certifications</h3>
        {entries.length === 0 && <p className="onboarding-step-label">No certifications added.</p>}
        {entries.map((c) => (
          <div key={c.id} className="onboarding-list-item">
            <div className="onboarding-list-meta">
              <div>
                <strong>{c.certification_name}</strong> — {c.issuing_organization}
              </div>
              {(c.specialization_name || c.subspecialization_name) && (
                <div className="onboarding-step-label">
                  {[c.specialization_name, c.subspecialization_name].filter(Boolean).join(' / ')}
                </div>
              )}
              <div className="onboarding-step-label">Issued: {c.issue_date}</div>
              {c.expiry_date ? (
                <div className="onboarding-step-label">Expiry: {c.expiry_date}</div>
              ) : null}
              {c.certificate_number ? (
                <div className="onboarding-step-label">Certificate No: {c.certificate_number}</div>
              ) : null}
              {getFileNameFromUrl(c.certificate_document_url || undefined) && (
                <div className="onboarding-step-label onboarding-file-name">
                  File: {getFileNameFromUrl(c.certificate_document_url || undefined)}
                </div>
              )}
            </div>
            <button type="button" className="btn-secondary" onClick={() => onDelete(c.id)}>
              Remove
            </button>
          </div>
        ))}
      </div>

      <div className="step-actions">
        <button type="button" className="btn-secondary" onClick={onBack}>
          Back
        </button>
        <button type="button" className="btn-primary" onClick={onNext}>
          Continue
        </button>
      </div>
    </div>
  );
}
